import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { HostInfo, ApiResponse, UpgradeToProRequest } from '../../types';
import gameService from '../../services/gameService';

interface ProUpgradeProps {
  onUpgradeSuccess?: (hostInfo: HostInfo) => void;
}

const ProUpgrade: React.FC<ProUpgradeProps> = ({ onUpgradeSuccess }) => {
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [upgrading, setUpgrading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [maxGameSpaces, setMaxGameSpaces] = useState<number>(3);

  useEffect(() => {
    loadHostInfo();
  }, []);

  const loadHostInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await gameService.getHostInfo();
      
      if (response.success && response.data) {
        setHostInfo(response.data);
      } else {
        setError(response.message || 'Failed to load host information');
      }
    } catch (err) {
      setError('An unexpected error occurred while loading host information');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const request: UpgradeToProRequest = {
        maxGameSpaces: maxGameSpaces
      };
      
      const response = await gameService.upgradeToPro(request);
      
      if (response.success && response.data) {
        setHostInfo(response.data);
        setSuccess('Successfully upgraded to Pro! You can now create multiple game spaces.');
        
        if (onUpgradeSuccess) {
          onUpgradeSuccess(response.data);
        }
      } else {
        setError(response.message || 'Failed to upgrade to Pro');
      }
    } catch (err) {
      setError('An unexpected error occurred during the upgrade process');
      console.error(err);
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col md={8} className="mx-auto">
          <Card className="shadow-sm">
            <Card.Header as="h5" className="bg-primary text-white">
              {hostInfo?.isPro ? 'Pro Account Status' : 'Upgrade to Pro'}
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              
              {hostInfo && (
                <div className="mb-4">
                  <h6>Current Status</h6>
                  <p>
                    <strong>Account Type:</strong> {hostInfo.isProUser ? 'Pro User' : 'Regular Host'}<br />
                    <strong>Token Balance:</strong> {hostInfo.tokenBalance}<br />
                    <strong>Game Spaces:</strong> {hostInfo.currentGameSpaceCount} / {hostInfo.isProUser ? hostInfo.maxGameSpaces : '1 (limit)'}
                  </p>
                </div>
              )}
              
              {hostInfo && !hostInfo.isProUser && (
                <>
                  <h6>Upgrade to Pro</h6>
                  <p>
                    As a Pro user, you can create multiple game spaces and enjoy additional features.
                    Upgrade now to enhance your hosting experience!
                  </p>
                  
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Number of Game Spaces</Form.Label>
                      <Form.Control
                        type="number"
                        min={2}
                        max={10}
                        value={maxGameSpaces}
                        onChange={(e) => setMaxGameSpaces(parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">
                        Select how many game spaces you would like to have (2-10)
                      </Form.Text>
                    </Form.Group>
                    
                    <Button 
                      variant="primary" 
                      onClick={handleUpgrade}
                      disabled={upgrading}
                    >
                      {upgrading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Upgrading...
                        </>
                      ) : (
                        'Upgrade to Pro'
                      )}
                    </Button>
                  </Form>
                </>
              )}
              
              {hostInfo && hostInfo.isProUser && (
                <Alert variant="info">
                  You are already a Pro user with access to {hostInfo.maxGameSpaces} game spaces.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProUpgrade;
